/**
 * Data scopes: the one guard against writing data that became outdated (a
 * project switch, a language change, changed permissions, a logout) after an
 * await. An operation captures its scope when it starts and writes only
 * through it. Described in `.claude/skills/h-sw-channels/data-scope.md`.
 */
import { getProjectId } from 'serviceWorker/db/config';
import { DataOwner, DEFAULT_DATA_OWNER } from 'serviceWorker/db/dataOwner';
import { swSignals } from 'serviceWorker/events/swSignals';
import { HttpRequestModelType } from 'shared/domain/httpRequest/httpRequestModel';
import { withTimeout } from 'shared/utils/withTimeout';
import { logOutdatedWriteSkipped } from './dataLog';

export { DataOwner };

/**
 * Generations of the stored data, kept in memory. Each event that makes
 * stored data outdated starts a new one, in one place: operations started
 * under an older generation must not write.
 */
const generations: Record<DataOwner, number> = {
  [DataOwner.PROJECT]: 0,
  [DataOwner.USER]: 0,
};

function startGeneration(...owners: DataOwner[]): void {
  owners.forEach((owner) => {
    generations[owner] += 1;
  });
}

/** Starts a new generation on every event that makes stored data outdated. */
export function init(): () => void {
  const unsubscribes = [
    swSignals.projectDataReplaced.subscribe(() => startGeneration(DataOwner.PROJECT)),
    swSignals.permissionsChanged.subscribe(() => startGeneration(DataOwner.PROJECT)),
    swSignals.loggedOut.subscribe(() => startGeneration(DataOwner.PROJECT, DataOwner.USER)),
  ];
  return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
}

/**
 * The writes called and not ended yet, checks of their scope included. The
 * flush that follows a signal waits for them (`awaitInFlightWrites`): the
 * signal outdated the scopes, so a write called after it is skipped at once
 * and only the ones called before can still reach the tables. The body of a
 * write is local work only: it never waits for a request, the request
 * suspender or a flush, or the flush would wait for it in turn.
 */
const writesInFlight = new Set<Promise<unknown>>();

/**
 * Resolves `true` once the writes running now have ended, `false` after
 * `timeoutMs` with one still running, so the caller can report it and go on.
 * Call it after the signal that outdates the data.
 */
export function awaitInFlightWrites(timeoutMs: number): Promise<boolean> {
  const writesEnded = Promise.allSettled([...writesInFlight]).then((): true => true);
  return withTimeout(writesEnded, timeoutMs, (): false => false);
}

/** Counts the write as in flight until it ends, failed or not. */
function trackInFlightWrite<TResult>(writing: Promise<TResult>): Promise<TResult> {
  writesInFlight.add(writing);
  const forget = (): void => {
    writesInFlight.delete(writing);
  };
  void writing.then(forget, forget);
  return writing;
}

/**
 * The data an operation was started for. Writes go through it, so a write
 * after the data became outdated is skipped instead of reaching the data of
 * another project.
 */
export interface DataScope {
  /** The project the operation was started for; undefined for data of the user. */
  readonly projectId: string | undefined;
  isCurrent(): Promise<boolean>;
  /** Runs the write while the scope is current; otherwise skips it and resolves with undefined. */
  write<TResult>(write: () => Promise<TResult>): Promise<TResult | undefined>;
  /**
   * A write of a repository or service that runs through `write`, e.g.
   * `scope.guard(sitesService.add)`. A function, not the whole object: a
   * module namespace (`import * as service`) is frozen in the build and
   * cannot be proxied with wrapped functions. The function is called without
   * `this`: repositories and services are module functions.
   */
  guard<TArgs extends unknown[], TResult>(
    run: (...args: TArgs) => Promise<TResult>
  ): (...args: TArgs) => Promise<TResult | undefined>;
}

interface ScopeDescription {
  owner: DataOwner;
  /** What the scope was started for, for the log of a skipped write. */
  description: string;
}

function createDataScope(
  projectId: string | undefined,
  isCurrent: () => Promise<boolean>,
  { owner, description }: ScopeDescription
): DataScope {
  // One log per scope: an outdated operation skips all its remaining writes.
  let isSkipLogged = false;
  const writeWhileCurrent = async <TResult>(run: () => Promise<TResult>): Promise<TResult | undefined> => {
    if (await isCurrent()) {
      return run();
    }
    if (!isSkipLogged) {
      isSkipLogged = true;
      logOutdatedWriteSkipped({ description, owner, projectId });
    }
    return undefined;
  };

  const write = <TResult,>(run: () => Promise<TResult>): Promise<TResult | undefined> =>
    trackInFlightWrite(writeWhileCurrent(run));

  const guard =
    <TArgs extends unknown[], TResult>(run: (...args: TArgs) => Promise<TResult>) =>
    (...args: TArgs): Promise<TResult | undefined> =>
      write(() => run(...args));

  return { projectId, isCurrent, write, guard };
}

interface DataScopeOptions {
  owner: DataOwner;
  /** The project the operation calls the API for; ignored for data of the user. */
  projectId?: string;
  /** What the operation stores, for its logs (e.g. `sites`). */
  subject?: string;
}

function describeOwnerOfData(owner: DataOwner, projectId: string | undefined): string {
  if (owner === DataOwner.USER) {
    return 'the user';
  }
  return projectId ? `project ${projectId}` : 'the selected project';
}

/** The scope of an operation started now, in this run of the worker. */
export function captureDataScope({ owner, projectId, subject = 'data' }: DataScopeOptions): DataScope {
  const generationAtStart = generations[owner];
  const sessionAtStart = generations[DataOwner.USER];
  return createDataScope(
    owner === DataOwner.PROJECT ? projectId : undefined,
    async () => generations[owner] === generationAtStart && generations[DataOwner.USER] === sessionAtStart,
    { owner, description: `${subject} of ${describeOwnerOfData(owner, projectId)}` }
  );
}

/**
 * The scope of an operation the page asked for in the selected project. Its
 * `projectId` is the project at the start: follow-up requests use it, not the
 * project selected by then.
 */
export async function captureSelectedProjectScope(): Promise<DataScope> {
  // The generation first: a switch while the project is read makes the scope outdated.
  const scope = captureDataScope({ owner: DataOwner.PROJECT });
  const projectId = await getProjectId();
  return { ...scope, projectId };
}

/** A service table; the ones of data of the user declare `owner`. */
type CollectionService = object & { owner?: DataOwner };

/** Whom the collection of a service table belongs to, as its table declares. */
export function ownerOfService(service: object): DataOwner {
  // Checked with `in`: a table of a project declares no owner.
  return 'owner' in service ? ((service as CollectionService).owner ?? DEFAULT_DATA_OWNER) : DEFAULT_DATA_OWNER;
}

/** The scope of a download or sync of a collection, started now for the given project. */
export function captureCollectionScope(service: object, projectId: string | undefined, entityName?: string): DataScope {
  return captureDataScope({ owner: ownerOfService(service), projectId, subject: entityName });
}

/** Requests whose local data belongs to the user, not to a project. */
const USER_LEVEL_REQUEST_TYPES: readonly HttpRequestModelType[] = [
  HttpRequestModelType.project,
  HttpRequestModelType.organization,
];

/** `project/{id}/…`, with or without the leading slash (issue requests have none). */
const PROJECT_URL_PREFIX = /^\/?project\/([^/]+)(?:\/|$)/;

export interface RequestOfProject {
  entityType?: HttpRequestModelType;
  url?: string;
  data?: unknown;
}

/** The project a queued request changes data of; undefined for data of the user. */
export function projectIdOfRequest({ entityType, url, data }: RequestOfProject): string | undefined {
  if (entityType && USER_LEVEL_REQUEST_TYPES.includes(entityType)) {
    return undefined;
  }
  const projectIdOfData =
    typeof data === 'object' && data !== null && 'projectId' in data && typeof data.projectId === 'string'
      ? data.projectId
      : undefined;
  return PROJECT_URL_PREFIX.exec(url ?? '')?.[1] ?? projectIdOfData;
}

/**
 * The scope of data of a given project (an answer the page or the queue
 * waited for): current while that project is the configured one, compared
 * at each write. Data of the user (no project) is always current.
 */
export function scopeOfProject(projectId: string | undefined): DataScope {
  return createDataScope(projectId, async () => !projectId || projectId === (await getProjectId()), {
    owner: projectId ? DataOwner.PROJECT : DataOwner.USER,
    description: `answer of project ${projectId}`,
  });
}

/** The scope of a queued request: the queue outlives the worker, so the request's own project decides. */
export function scopeOfQueuedRequest(request: RequestOfProject): DataScope {
  return scopeOfProject(projectIdOfRequest(request));
}

/** The queued request being executed; the HTTP queue runs one request at a time. */
let executedRequestScope: DataScope | undefined;

/** Runs a queued request; the request runners' writes (`guardedByQueuedRequest`) are in its scope. */
export async function runQueuedRequest<TResult>(request: RequestOfProject, run: () => Promise<TResult>): Promise<TResult> {
  executedRequestScope = scopeOfQueuedRequest(request);
  try {
    return await run();
  } finally {
    executedRequestScope = undefined;
  }
}

/** A local write of a request runner, skipped when the executed request is of a project no longer selected. */
export function guardedByQueuedRequest<TArgs extends unknown[], TResult>(
  write: (...args: TArgs) => Promise<TResult>
): (...args: TArgs) => Promise<TResult | undefined> {
  return (...args) => (executedRequestScope ? executedRequestScope.write(() => write(...args)) : write(...args));
}
